import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText,
  ChevronLeft,
  ChevronRight,
  Loader2,
  User,
  QrCode,
  CreditCard,
  Bell
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

const getActionIcon = (targetType) => {
  switch (targetType) {
    case 'user':
      return User;
    case 'qr_code':
      return QrCode;
    case 'payment':
      return CreditCard;
    case 'notification':
      return Bell;
    default:
      return FileText;
  }
};

const getActionColor = (action) => {
  if (action.includes('enabled') || action.includes('paid')) {
    return 'text-emerald-400 bg-emerald-500/20';
  }
  if (action.includes('disabled') || action.includes('expired')) {
    return 'text-red-400 bg-red-500/20';
  }
  if (action.includes('extended')) {
    return 'text-blue-400 bg-blue-500/20';
  }
  if (action.includes('notification')) {
    return 'text-purple-400 bg-purple-500/20';
  }
  return 'text-slate-400 bg-slate-500/20';
};

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('adminToken');
        const response = await axios.get(`${API}/admin/audit-logs?page=${page}&limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setLogs(response.data.logs);
        setTotalPages(response.data.pages || response.data.totalPages || 1);
        setTotal(response.data.total);
      } catch (error) {
        toast.error('Failed to load audit logs');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [page]);

  const formatAction = (action) => {
    return action
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Logs</h1>
          <p className="text-slate-400">Track all admin actions on the platform</p>
        </div>

        {/* Stats */}
        <div className="text-slate-400">
          Total: {total} logged actions
        </div>

        {/* Logs List */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 text-slate-500 animate-spin mx-auto" />
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No audit logs found
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {logs.map((log) => {
                const Icon = getActionIcon(log.target_type);
                const colorClass = getActionColor(log.action);
                
                return (
                  <div key={log.id} className="p-4 hover:bg-slate-800/50">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm font-medium px-2 py-1 rounded ${colorClass}`}>
                            {formatAction(log.action)}
                          </span>
                          <span className="text-slate-400 text-sm">
                            on {log.target_type}
                          </span>
                        </div>
                        <p className="text-white mt-1">
                          <span className="text-slate-400">Admin:</span> {log.admin_email}
                        </p>
                        <p className="text-slate-500 text-sm mt-1 font-mono">
                          Target: {log.target_id ? `${log.target_id.substring(0, 20)}...` : 'N/A'}
                        </p>
                        {log.details && (
                          <p className="text-slate-400 text-sm mt-1">
                            Details: {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                          </p>
                        )}
                        <p className="text-slate-500 text-xs mt-2">
                          {new Date(log.created_at).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="text-slate-400">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAuditLogs;
